import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import NoteAddIcon from '@mui/icons-material/NoteAdd';

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    backgroundColor: 'var(--al-surface)',
    color: 'white',
    borderRadius: 0,
    minWidth: '400px',
    maxWidth: '560px',
    border: '1px solid rgba(var(--al-primary-rgb), 0.3)'
  }
}));

const StyledDialogTitle = styled(DialogTitle)({
  backgroundColor: 'var(--al-bg)',
  color: 'var(--al-primary)',
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 700,
  fontSize: '0.9rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
});

const StyledDialogContent = styled(DialogContent)({
  padding: '20px',
  backgroundColor: 'var(--al-surface)'
});

const NotesBody = styled(Box)({
  backgroundColor: '#363636',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  padding: '14px 16px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '0.9rem',
  lineHeight: 1.55,
  color: 'rgba(255, 255, 255, 0.9)',
  minHeight: '80px'
});

const CloseButton = styled(Button)({
  backgroundColor: 'var(--al-primary)',
  color: 'var(--al-bg)',
  fontWeight: 600,
  textTransform: 'uppercase',
  borderRadius: 0,
  padding: '8px 20px',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: '0.75rem',
  '&:hover': {
    backgroundColor: '#7FE87F'
  }
});

/**
 * Read-only view of order-level notes (shared across all lines on the order).
 */
const OrderNotesModal = ({
  isOpen,
  onClose,
  notes,
  orderNumber,
  customerName
}) => {
  const text = String(notes || '').trim();

  return (
    <StyledDialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NoteAddIcon sx={{ fontSize: '1.2rem' }} />
          Order Notes
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            padding: '4px',
            '&:hover': { color: 'var(--al-primary)' }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {(orderNumber || customerName) && (
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.8rem',
              mb: 2,
              textTransform: 'uppercase'
            }}
          >
            {orderNumber ? (
              <>
                Order:{' '}
                <span style={{ color: 'var(--al-primary)' }}>{orderNumber}</span>
              </>
            ) : null}
            {orderNumber && customerName ? ' · ' : null}
            {customerName ? (
              <>
                Customer:{' '}
                <span style={{ color: 'var(--al-primary)' }}>{customerName}</span>
              </>
            ) : null}
          </Typography>
        )}

        <NotesBody>
          {text || 'No order notes.'}
        </NotesBody>
      </StyledDialogContent>

      <DialogActions
        sx={{
          backgroundColor: 'var(--al-bg)',
          padding: '12px 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <CloseButton onClick={onClose}>Close</CloseButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default OrderNotesModal;
